<template>
  <div class="min-h-screen bg-background flex items-center justify-center px-4 py-12">
    <div class="max-w-md w-full">
      <div class="text-center mb-8">
        <router-link to="/" class="inline-block">
          <div class="h-12 w-48 overflow-hidden rounded-lg mx-auto mb-4">
            <img
              src="/images/bi-logo.jpg"
              alt="Brand Integrated"
              class="h-full w-full object-contain object-center"
            />
          </div>
        </router-link>
        <h1 class="text-3xl font-display font-medium text-on-background mb-2">
          Reset Password
        </h1>
        <p class="text-on-surface-variant">
          Enter your new password
        </p>
      </div>

      <div class="bg-surface rounded-xl shadow-lg border border-outline-variant/30 p-8">
        <div
          v-if="invalidToken"
          class="bg-error-container border border-error/30 text-error px-4 py-3 rounded-lg text-sm mb-6 font-medium"
        >
          Invalid or expired reset token. Please request a new one.
        </div>

        <div
          v-if="success"
          class="bg-primary/10 border border-primary/30 text-primary px-4 py-3 rounded-lg text-sm mb-6 font-medium"
        >
          {{ success }}
        </div>

        <form v-if="!invalidToken && !success" @submit.prevent="handleResetPassword" class="space-y-6">
          <div
            v-if="error"
            class="bg-error-container border border-error/30 text-error px-4 py-3 rounded-lg text-sm font-medium"
          >
            {{ error }}
          </div>

          <div>
            <label
              for="newPassword"
              class="block text-sm font-medium text-on-background mb-2"
            >
              New Password
            </label>
            <input
              id="newPassword"
              v-model="newPassword"
              type="password"
              required
              autocomplete="new-password"
              minlength="6"
              class="w-full px-4 py-3 rounded-lg border border-outline-variant bg-surface text-on-background placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              placeholder="Enter new password"
              :disabled="isLoading"
            />
          </div>

          <div>
            <label
              for="confirmPassword"
              class="block text-sm font-medium text-on-background mb-2"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              required
              autocomplete="new-password"
              minlength="6"
              class="w-full px-4 py-3 rounded-lg border border-outline-variant bg-surface text-on-background placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              placeholder="Confirm new password"
              :disabled="isLoading"
            />
          </div>

          <div
            v-if="passwordMismatch"
            class="bg-surface-container border border-outline-variant text-on-surface-variant px-4 py-3 rounded-lg text-sm font-medium"
          >
            Passwords do not match
          </div>

          <button
            type="submit"
            :disabled="isLoading || passwordMismatch"
            class="w-full bg-primary hover:bg-primary-container text-on-primary font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="!isLoading">Reset Password</span>
            <span v-else class="flex items-center justify-center">
              <span class="mr-2">Resetting...</span>
              <span class="animate-spin">⟳</span>
            </span>
          </button>
        </form>

        <div v-if="success" class="mt-6 text-center">
          <router-link
            to="/admin/login"
            class="text-sm text-primary hover:text-primary-container transition-colors"
          >
            Go to Login →
          </router-link>
        </div>
      </div>

      <div v-if="!success" class="text-center mt-6 space-y-2">
        <router-link
          to="/admin/login"
          class="block text-sm text-on-surface-variant hover:text-primary transition-colors"
        >
          ← Back to Login
        </router-link>
        <router-link
          to="/"
          class="block text-sm text-on-surface-variant hover:text-primary transition-colors"
        >
          ← Back to Home
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { authService } from '../../utils/auth.js';

const router = useRouter();
const route = useRoute();

const newPassword = ref('');
const confirmPassword = ref('');
const error = ref('');
const success = ref('');
const invalidToken = ref(false);
const isLoading = ref(false);
const resetToken = ref('');

const passwordMismatch = computed(() => {
  return newPassword.value && confirmPassword.value && newPassword.value !== confirmPassword.value;
});

onMounted(async () => {
  resetToken.value = route.query.token || '';
  
  if (!resetToken.value) {
    invalidToken.value = true;
    return;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('Reset token from URL:', resetToken.value);
    const sessionData = sessionStorage.getItem('password_reset_token');
    if (sessionData) {
      const data = JSON.parse(sessionData);
      console.log('Token from sessionStorage:', data.token);
      console.log('Tokens match:', resetToken.value === data.token);
    }
  }

  const verification = await authService.verifyResetToken(resetToken.value);
  if (!verification.valid) {
    invalidToken.value = true;
  }
});

const handleResetPassword = async () => {
  error.value = '';
  
  if (passwordMismatch.value) {
    error.value = 'Passwords do not match';
    return;
  }

  isLoading.value = true;

  try {
    const result = await authService.resetPassword(resetToken.value, newPassword.value);
    
    if (result.success) {
      success.value = result.message;
      setTimeout(() => {
        router.push('/admin/login');
      }, 2000);
    } else {
      error.value = result.error || 'Failed to reset password. Please try again.';
    }
  } catch (err) {
    error.value = 'An error occurred. Please try again.';
  } finally {
    isLoading.value = false;
  }
};
</script>
